import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAdminEarnings } from "../../../redux/actions/profile";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from "recharts";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";
const ACCENT2 = "#4fb0c6";
const ACCENT3 = "#f4a261";

const PERIODS = [
  { label: "This Month", value: "this_month" },
  { label: "Last Month", value: "last_month" },
  { label: "Last 3 Months", value: "3_months" },
  { label: "This Year", value: "year" },
  { label: "Custom Range", value: "custom" },
];

function getRange(period) {
  const now = new Date();
  let from, to;
  if (period === "this_month") {
    from = new Date(now.getFullYear(), now.getMonth(), 1); to = now;
  } else if (period === "last_month") {
    from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    to = new Date(now.getFullYear(), now.getMonth(), 0);
  } else if (period === "3_months") {
    from = new Date(now.getFullYear(), now.getMonth() - 3, 1); to = now;
  } else if (period === "year") {
    from = new Date(now.getFullYear(), 0, 1); to = now;
  }
  return { from: from?.toISOString().split("T")[0], to: to?.toISOString().split("T")[0] };
}

function EarningsCard({ label, value, icon, accent, sub }) {
  const c = accent || PRIMARY;
  return (
    <div style={{ background: "#fff", borderRadius: 10, padding: "18px 20px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", display: "flex", alignItems: "flex-start", gap: 14, borderTop: `3px solid ${c}` }}>
      <div style={{ width: 46, height: 46, borderRadius: 10, background: c + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <i className={icon} style={{ fontSize: 20, color: c }} />
      </div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 800, color: BG, lineHeight: 1.2 }}>{value ?? "—"}</div>
        <div style={{ fontSize: 12, color: "#888", marginTop: 3 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "#bbb", marginTop: 3 }}>{sub}</div>}
      </div>
    </div>
  );
}

export default function Earnings() {
  const dispatch = useDispatch();
  const data = useSelector((s) => s.profile.adminEarnings);

  const [period, setPeriod] = useState("this_month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const load = () => {
    const params = { groupBy: "month" };
    if (period !== "custom") {
      const range = getRange(period);
      if (range.from) params.from = range.from;
      if (range.to) params.to = range.to;
    } else {
      if (customFrom) params.from = customFrom;
      if (customTo) params.to = customTo;
    }
    dispatch(getAdminEarnings(params));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [period, dispatch]);

  const fmtMoney = (n) => n != null ? `₦${Number(n).toLocaleString("en-NG")}` : "—";

  if (!data) {
    return (
      <div style={S.loading}>
        <div style={S.spinner} />
        <p style={{ color: "#888", marginTop: 16, fontSize: 14 }}>Loading earnings…</p>
      </div>
    );
  }

  const chartData = (data.monthlyBreakdown || []).map((m) => ({
    month: m.month,
    "Subscription Revenue": m.subscriptionRevenue || 0,
    "TX Fee Earnings": data.debitCount ? Math.round(data.txFeeEarnings / Math.max(data.debitCount, 1)) : 0,
  }));

  return (
    <div style={S.root}>
      <div style={S.pageHeader}>
        <div>
          <h2 style={S.heading}>Platform Earnings</h2>
          <p style={S.headingSub}>Revenue from subscriptions, transaction fees and agent commissions</p>
        </div>
        <div style={S.liveTag}>
          <span style={S.liveDot} />Live Data
        </div>
      </div>

      {/* Period selector */}
      <div style={S.periodBar}>
        {PERIODS.map((p) => (
          <button key={p.value} style={{ ...S.periodBtn, background: period === p.value ? BG : "#fff", color: period === p.value ? "#fff" : "#555", border: period === p.value ? `1.5px solid ${BG}` : "1.5px solid #e0e0e0" }} onClick={() => setPeriod(p.value)}>
            {p.label}
          </button>
        ))}
        {period === "custom" && (
          <>
            <input style={S.dateInput} type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            <input style={S.dateInput} type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            <button style={S.applyBtn} onClick={load}>Apply</button>
          </>
        )}
      </div>

      {/* Summary cards */}
      <div style={S.sectionLabel}>
        <i className="bi bi-graph-up" style={{ color: PRIMARY, marginRight: 8 }} />Revenue Breakdown
      </div>
      <div style={S.cardGrid}>
        <EarningsCard label="Subscription Revenue" value={fmtMoney(data.subscriptionRevenue)} icon="bi bi-cash" accent={PRIMARY} sub={`${data.paymentCount} successful payments`} />
        <EarningsCard label="Paystack Fees Deducted" value={fmtMoney(data.paystackFees)} icon="bi bi-dash-circle" accent="#e63946" sub="Est. 1.5%, capped at ₦2,000 per txn" />
        <EarningsCard label="Agent Commissions Paid" value={fmtMoney(data.totalAgentCommissions)} icon="bi bi-person-badge" accent={ACCENT3} sub="15% of referred subscription revenue" />
        <EarningsCard label="TX Fee Earnings" value={fmtMoney(data.txFeeEarnings)} icon="bi bi-arrow-left-right" accent={ACCENT2} sub={`${data.debitCount} transfers processed`} />
        <EarningsCard label="Net Platform Earnings" value={fmtMoney(data.netEarnings)} icon="bi bi-wallet2" accent="#9b5de5" sub="Revenue − fees − commissions + TX fees" />
      </div>

      {/* Monthly chart */}
      {chartData.length > 0 && (
        <>
          <div style={S.sectionLabel}>
            <i className="bi bi-bar-chart-fill" style={{ color: PRIMARY, marginRight: 8 }} />Monthly Breakdown
          </div>
          <div style={S.chartCard}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fill: "#888", fontSize: 12 }} />
                <YAxis tick={{ fill: "#888", fontSize: 12 }} tickFormatter={(v) => `₦${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                  formatter={(v) => [`₦${Number(v).toLocaleString("en-NG")}`, ""]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Subscription Revenue" fill={PRIMARY} radius={[4, 4, 0, 0]} />
                <Bar dataKey="TX Fee Earnings" fill={ACCENT2} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/* Agent commissions table */}
      {data.agentBreakdown?.length > 0 && (
        <>
          <div style={S.sectionLabel}>
            <i className="bi bi-people-fill" style={{ color: PRIMARY, marginRight: 8 }} />Agent Commissions Breakdown
          </div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  {["Agent", "Email", "Merchants Referred", "Total Commission Earned"].map((h) => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.agentBreakdown.map((a, i) => (
                  <tr key={i} style={S.tr}>
                    <td style={{ ...S.td, fontWeight: 600, color: BG }}>{a.agent || "—"}</td>
                    <td style={S.td}>{a.email}</td>
                    <td style={S.td}>{a.merchantsReferred}</td>
                    <td style={{ ...S.td, fontWeight: 700, color: ACCENT3 }}>{fmtMoney(a.totalEarned)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const S = {
  root: { paddingBottom: 40 },
  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 28 },
  heading: { fontSize: 24, fontWeight: 800, color: BG, margin: 0 },
  headingSub: { fontSize: 13, color: "#888", marginTop: 4 },
  liveTag: { display: "flex", alignItems: "center", gap: 6, background: "rgba(167,204,72,0.12)", border: "1px solid " + PRIMARY, borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 600, color: "#5a8a00" },
  liveDot: { width: 7, height: 7, borderRadius: "50%", background: PRIMARY },
  sectionLabel: { fontSize: 13, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1, marginBottom: 14, marginTop: 8, display: "flex", alignItems: "center" },
  periodBar: { background: "#fff", borderRadius: 10, padding: "14px 18px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 28, alignItems: "center" },
  periodBtn: { borderRadius: 8, padding: "7px 16px", cursor: "pointer", fontSize: 13, fontWeight: 500 },
  dateInput: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "7px 10px", fontSize: 13, color: "#333", outline: "none", background: "#fafafa" },
  applyBtn: { background: BG, border: "none", borderRadius: 8, color: "#fff", fontWeight: 600, padding: "7px 18px", cursor: "pointer", fontSize: 13 },
  cardGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 16, marginBottom: 32 },
  chartCard: { background: "#fff", borderRadius: 10, padding: "20px 18px 12px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", marginBottom: 28 },
  tableWrap: { background: "#fff", borderRadius: 10, overflowX: "auto", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", WebkitOverflowScrolling: "touch", marginBottom: 28 },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 600 },
  th: { background: BG, color: "#fff", padding: "11px 14px", textAlign: "left", fontSize: 12, fontWeight: 600 },
  tr: { borderBottom: "1px solid #f5f5f5" },
  td: { padding: "11px 14px", fontSize: 13, color: "#555", verticalAlign: "middle" },
  loading: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 360 },
  spinner: { width: 36, height: 36, borderRadius: "50%", border: "3px solid " + PRIMARY, borderTopColor: "transparent" },
};
